<script setup lang="ts">
import { ref, computed, watch, watchEffect, inject } from 'vue'
import { CheckIcon, PlusIcon, XMarkIcon } from '@heroicons/vue/20/solid'
import { onClickOutside } from '@vueuse/core'
import CrdColorPicker from '@/components/common/crd-color-picker/CrdColorPicker.vue'
import { generateRainbowColorsInHsl } from '@/utils/color.util.ts'
import { isNull, isUndefined } from 'lodash'
import { ErrorMessage, Field, type PrivateFormContext, type useForm } from 'vee-validate'
import { ProjectSettingsField, ProvideElement } from '@/enums.ts'
import type { ColorPickerColor, ProjectSettings } from '@/components/projects/interfaces.ts'
import ProjectSettingsForm from '@/components/projects/ProjectSettingsForm.vue'
import { ProjectSettingFormKey } from '@/components/projects/settings.types.ts'

const props = defineProps<{
  name: string
  modelValue?: string | null
  isColorEnable: boolean
}>()

const emit = defineEmits(['update:modelValue', 'color-selected'])

const color = computed({
  get: () => props.modelValue,
  set: (newValue: string) => {
    emit('update:modelValue', newValue)
    emit('color-selected')
  },
})

const selectedColorIndex = ref<number | null>(null)
const tempColor = ref<string>(color.value || '')
const pickerWrapperRef = ref<HTMLDivElement | null>(null)
const hasCustomColor = ref<boolean>(false)
const items = ref<string[]>(generateRainbowColorsInHsl())
const { defineField } = inject(ProjectSettingFormKey)


const isColorPickerVisible = ref<boolean>(false)
const [ widgetVisualColor ] = defineField(ProjectSettingsField.WidgetVisualColor)

function onSelectColorItem(index: number): void {
  if (index === -1) {
    // Custom color selected
    selectedColorIndex.value = -1
    if (tempColor.value) {
      color.value = tempColor.value
    }
  } else if (items.value[index]) {
    selectedColorIndex.value = index
    color.value = items.value[index]
  }
}

function onDeleteCustomColor(): void {
  hasCustomColor.value = false
  if (isNull(selectedColorIndex.value) || selectedColorIndex.value < 0) {
    onSelectColorItem(0)
  }
}

watchEffect(() => {
  if (props.modelValue) {
    const index = items.value.findIndex((item) => item === props.modelValue)
    if (index >= 0) {
      selectedColorIndex.value = index
    } else {
      hasCustomColor.value = true
      tempColor.value = props.modelValue
      selectedColorIndex.value = -1
    }
  } else if (items.value.length > 0) {
    onSelectColorItem(0)
  }
})

watch(
  () => tempColor.value,
  (newValue) => {
    if (!isNull(newValue) && !isUndefined(newValue)) {
      const index = items.value.findIndex((item) => item === newValue)

      if (index >= 0) {
        selectedColorIndex.value = index
      } else {
        hasCustomColor.value = true
        tempColor.value = newValue
        selectedColorIndex.value = -1
      }
    } else {
      selectedColorIndex.value = null
      tempColor.value = ''
    }
  },
)

function updateColor(newColor: string | undefined): void {
  if (!isUndefined(newColor)) {
    tempColor.value = newColor
    color.value = newColor
  }
}

onClickOutside(pickerWrapperRef, () => {
  if (isColorPickerVisible.value) {
    // onCancelColorAdd()
  }
})


</script>

<template>
  <div class="relative">
    <Field :name="name" v-model="color">
      <div class="flex items-center p-2 flex-wrap gap-2 max-w-full">
        <div class="flex flex-wrap gap-2.5 items-center">
          <div
            v-if="hasCustomColor"
            class="flex flex-col gap-2"
            :class="{ 'color-enable': !isColorEnable }"
          >
            <div
              class="color-swatch relative"
              :class="{ selected: selectedColorIndex === -1 }"
              :style="{ backgroundColor: tempColor }"
              @click="onSelectColorItem(-1)"
            >
              <CheckIcon v-if="selectedColorIndex === -1" class="check-icon" />
              <XMarkIcon class="delete-icon" @click.stop="onDeleteCustomColor" />
            </div>
          </div>

          <div v-else class="flex flex-col gap-2">
            <button
              type="button"
              class="color-swatch add-button"
              @click="() => {
                isColorPickerVisible = true

                if (!tempColor && items.length > 0 && items[0]) {
                  tempColor = items[0]
                }
              }"
              :class="{ active: isColorPickerVisible }"
            >
              <PlusIcon class="w-6 h-6 text-gray-400" />
            </button>
          </div>

          <div
            v-for="(item, index) in items"
            :key="index"
            class="color-swatch"
            :class="{
              selected: selectedColorIndex === index,
              'border-fuchsia scale-105': selectedColorIndex === index && !isColorEnable,
              'border-gray-300': selectedColorIndex !== index,
              'shadow-xs': selectedColorIndex !== index,
              'color-enable': !isColorEnable,
            }"
            :style="{ backgroundColor: item }"
            @click="onSelectColorItem(index)"
          >
            <CheckIcon v-if="selectedColorIndex === index" class="check-icon" />
          </div>
        </div>
      </div>

      <div v-if="isColorPickerVisible" ref="pickerWrapperRef" class="absolute top-12 left-6 z-10">
        <CrdColorPicker
          @cancel="() => {
            isColorPickerVisible = false
            onDeleteCustomColor()
          }"
          @saved="(color: ColorPickerColor) => {
            if (tempColor) {
              hasCustomColor = true
              onSelectColorItem(-1) // Select custom color
            }

             isColorPickerVisible = false
            widgetVisualColor = color.hex
          }"
          @change="(color: ColorPickerColor) => {
            widgetVisualColor = color.hex
          }"
        />
      </div>
    </Field>

    <ErrorMessage
      :name="name"
      class="text-sm font-500 text-red-400 absolute top-full text-center w-full"
    />
  </div>
</template>

<style scoped>
@reference "../../../../index.css";

.color-swatch {
  @apply size-8 md:size-14 rounded-full cursor-pointer border-3 border-transparent relative transition-all duration-200 ease-in-out;
  @apply hover:scale-105 hover:shadow-md;
}

.color-swatch.selected {
  @apply border-white shadow-[0_0_0_2px_theme(colors.purple.500)];
}

.color-enable {
  @apply opacity-50 hover:opacity-100;
}

.add-button {
  @apply bg-gray-100 border-dashed border-2 border-gray-300 flex items-center justify-center shadow-none;
  @apply hover:border-gray-400 hover:bg-gray-50 hover:scale-100 hover:shadow-none;
}

.add-button.active {
  @apply border-purple-500 bg-purple-50;
}

.check-icon {
  @apply w-5 h-5 text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2;
  filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.4));
}

.delete-icon {
  @apply w-5 h-5 text-red-500 absolute -top-2.5 -right-2.5 bg-white rounded-full p-0.5;
  @apply hover:bg-red-50;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
</style>
